import { useEffect, useRef, useState } from 'react'

// Buffers ≤ this many characters render immediately (zero latency on fast streams).
// Larger buffers are drained over ~DRAIN_FRAMES frames to smooth bursty network delivery.
const INSTANT_CHARS = 5
const DRAIN_FRAMES = 20

/**
 * Adaptive-drain text rendering for streaming LLM output.
 *
 * Fast streams (tokens arrive 1-3 chars at a time): remaining is almost always
 * ≤ INSTANT_CHARS, so content renders immediately — indistinguishable from raw RAF.
 *
 * Bursty streams (proxy batches 50-200 chars per chunk): drainRate scales with
 * buffer depth so any burst finishes in ≈DRAIN_FRAMES (~250ms). The user sees
 * smooth text flow instead of visible jumps.
 *
 * When isStreaming is false, content is returned directly — no state, no effect.
 */
export function useRafText(
  content: string,
  isStreaming: boolean,
): { displayText: string } {
  const [displayText, setDisplayText] = useState(content)
  const rafRef = useRef<number | null>(null)
  const contentRef = useRef(content)
  const displayRef = useRef(content)

  useEffect(() => {
    contentRef.current = content

    if (!isStreaming) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return
    }

    if (rafRef.current !== null) return

    function tick() {
      rafRef.current = null
      const full = contentRef.current
      const shown = displayRef.current

      // Content shrunk (new message / reset) — snap to current immediately
      if (full.length < shown.length) {
        displayRef.current = full
        setDisplayText(full)
        return
      }

      const remaining = full.length - shown.length
      if (remaining <= 0) return

      let next: string
      if (remaining <= INSTANT_CHARS) {
        next = full
      } else {
        const drainRate = Math.max(
          INSTANT_CHARS,
          Math.ceil(remaining / DRAIN_FRAMES),
        )
        next = full.slice(0, shown.length + drainRate)
      }

      displayRef.current = next
      setDisplayText(next)

      if (next.length < full.length) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [content, isStreaming])

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [])

  return { displayText: isStreaming ? displayText : content }
}
